import { Box, Button, Container, Group, Stack, Text, ThemeIcon, Title } from '@mantine/core'
import { createFileRoute, useNavigate } from '@tanstack/react-router'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { LuInbox, LuTriangleAlert } from 'react-icons/lu'
import { BookingTable } from '@/frontend/components/marina/BookingTable'
import { PillNav } from '@/frontend/components/marina/PillNav'
import { useCanWrite } from '@/frontend/hooks/useAuth'
import { type BookingStatus, useBookingStatusCounts, useBookings } from '@/frontend/hooks/useBookings'

type TabValue = 'ALL' | Extract<BookingStatus, 'PENDING' | 'WAITLISTED' | 'CONFIRMED' | 'DECLINED'>

const VALID: TabValue[] = ['ALL', 'PENDING', 'WAITLISTED', 'CONFIRMED', 'DECLINED']

export const Route = createFileRoute('/dashboard/reservations/requests')({
  validateSearch: (s: Record<string, unknown>): { tab?: TabValue } => ({
    tab: VALID.includes(s.tab as TabValue) ? (s.tab as TabValue) : undefined,
  }),
  component: RequestsList,
})

function RequestsList() {
  const { t } = useTranslation()
  const navigate = useNavigate({ from: Route.fullPath })
  const canWrite = useCanWrite()
  const { tab: rawTab } = Route.useSearch()
  const tab = rawTab ?? 'ALL'
  const setTab = (v: TabValue) => navigate({ search: () => ({ tab: v }), replace: true })
  const [search, setSearch] = useState('')
  const [query, setQuery] = useState('')
  const [page, setPage] = useState(1)
  const pageSize = 15

  const { data, isLoading } = useBookings({
    q: query,
    view: 'requests',
    status: tab === 'ALL' ? undefined : tab,
    page,
    pageSize,
  })
  const { data: counts } = useBookingStatusCounts()

  const badge = (status: TabValue) => {
    if (status === 'ALL')
      return ['PENDING', 'WAITLISTED', 'CONFIRMED', 'DECLINED'].reduce((s, k) => s + (counts?.counts[k] ?? 0), 0)
    return counts?.counts[status] ?? 0
  }

  const pendingCount = counts?.counts.PENDING ?? 0
  const waitlistedCount = counts?.counts.WAITLISTED ?? 0

  const emptyLabel =
    tab === 'PENDING'
      ? 'Tidak ada permintaan yang menunggu persetujuan'
      : tab === 'WAITLISTED'
        ? 'Tidak ada permintaan dalam daftar tunggu'
        : tab === 'CONFIRMED'
          ? 'Tidak ada permintaan yang dikonfirmasi'
          : tab === 'DECLINED'
            ? 'Tidak ada permintaan yang ditolak'
            : 'Belum ada permintaan booking'

  return (
    <Container size="xl" py="md">
      <Stack gap="md">
        {/* Header */}
        <Group justify="space-between" align="flex-start" wrap="nowrap">
          <Group gap="sm" align="center">
            <ThemeIcon variant="light" color="laut" size="lg" radius="md">
              <LuInbox size={18} />
            </ThemeIcon>
            <div>
              <Title order={2} ff="var(--font-display)" fw={600}>
                {t('page.reservations.requests.title')}
              </Title>
              <Text size="xs" c="dimmed">
                {t('page.reservations.requests.subtitle')}
              </Text>
            </div>
          </Group>
          {canWrite && (
            <Button color="karang" size="sm" onClick={() => navigate({ to: '/dashboard/bookings/new' as any })}>
              {t('page.reservations.requests.btn_new')}
            </Button>
          )}
        </Group>

        {/* Urgent alert — pending requests needing attention */}
        {(pendingCount > 0 || waitlistedCount > 0) && (
          <Box
            px="md"
            py="xs"
            style={{
              borderRadius: 10,
              background: 'light-dark(var(--mantine-color-karang-0), rgba(224,120,86,0.1))',
              cursor: 'pointer',
            }}
            onClick={() => setTab('PENDING')}
          >
            <Group gap="xs" justify="space-between" wrap="nowrap">
              <Group gap="xs">
                <ThemeIcon variant="light" color="karang" size="sm" radius="xl">
                  <LuTriangleAlert size={13} />
                </ThemeIcon>
                <Text size="sm" fw={600} c="karang.7">
                  {pendingCount > 0 && t('page.reservations.requests.alert_pending', { n: pendingCount })}
                  {pendingCount > 0 && waitlistedCount > 0 && ' · '}
                  {waitlistedCount > 0 && t('page.reservations.requests.alert_waitlisted', { n: waitlistedCount })}
                </Text>
              </Group>
              <Text size="xs" c="karang.6" fw={500}>
                {t('common.see_more')}
              </Text>
            </Group>
          </Box>
        )}

        {/* Filter + search */}
        <Group justify="space-between" wrap="wrap" gap="sm">
          <PillNav
            value={tab}
            onChange={(v) => {
              setTab(v)
              setPage(1)
            }}
            items={[
              {
                value: 'ALL',
                label: t('page.reservations.requests.tab_all'),
                count: badge('ALL') || undefined,
                countColor: 'gray',
              },
              {
                value: 'PENDING',
                label: t('page.reservations.requests.tab_pending'),
                count: badge('PENDING'),
                countColor: 'karang',
              },
              {
                value: 'WAITLISTED',
                label: t('page.reservations.requests.tab_waitlisted'),
                count: badge('WAITLISTED'),
                countColor: 'orange',
              },
              {
                value: 'CONFIRMED',
                label: t('page.reservations.requests.tab_confirmed'),
                count: badge('CONFIRMED'),
                countColor: 'laut',
              },
              {
                value: 'DECLINED',
                label: t('page.reservations.requests.tab_declined'),
                count: badge('DECLINED'),
                countColor: 'red',
              },
            ]}
          />
          <input
            type="search"
            value={search}
            placeholder={t('page.reservations.requests.placeholder')}
            onChange={(e) => setSearch(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                setQuery(search)
                setPage(1)
              }
            }}
            style={{
              fontSize: '0.8rem',
              padding: '6px 12px',
              borderRadius: 8,
              border: '1px solid var(--mantine-color-default-border)',
              background: 'var(--app-card-bg)',
              color: 'inherit',
              width: 220,
              outline: 'none',
            }}
          />
        </Group>

        <BookingTable
          items={data?.items ?? []}
          total={data?.total ?? 0}
          isLoading={isLoading}
          page={page}
          pageSize={pageSize}
          onPageChange={setPage}
          emptyLabel={emptyLabel}
          highlightPending
        />
      </Stack>
    </Container>
  )
}
